'use client';

import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';

import { Avatar } from '@/components';
import { useT } from '@/hooks';
import { EventResponse } from '@/types';
import { parseLiveScore } from '@/utils';

interface DefaultScoreBoardProps {
  game: EventResponse;
}

export const DefaultScoreBoard = ({ game }: DefaultScoreBoardProps) => {
  const { t } = useT();
  const scores = parseLiveScore(game.score);
  const homeScore = scores?.home || game.homeTeam?.score || '0';
  const awayScore = scores?.away || game.awayTeam?.score || '0';

  const formatPeriod = (period?: string) => {
    if (!period) return t('sports.live').toUpperCase();
    if (period.includes('/')) {
      const [current, total] = period.split('/');
      return `Game ${current} • BEST OF ${total}`;
    }
    return period;
  };

  const isLive = game.isLive;
  const isEnded = game.ended;
  const isLiveOrEnded = isLive || isEnded;
  const status = isLive
    ? formatPeriod(game.period)
    : isEnded
      ? t('sports.final', { defaultValue: 'FINAL' }).toUpperCase()
      : t('sports.upcoming', { defaultValue: 'UPCOMING' }).toUpperCase();
  const isDraw = String(homeScore) === String(awayScore);
  const hasStartTime = !!game.startTime;
  const startTimeObj = game.startTime ? new Date(game.startTime) : null;

  const formattedElapsed = game.elapsed
    ? game.elapsed.includes(':')
      ? game.elapsed
      : `${game.elapsed}`
    : '';

  const StatusDisplay = isLiveOrEnded ? (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.75rem' },
          }}
        >
          {homeScore}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' },
          }}
        >
          -
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.75rem' },
          }}
        >
          {awayScore}
        </Typography>
      </Box>
      {isLive ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1,
            color: '#FF4D4D',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.05em',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
            }}
          >
            {t('sports.live').toUpperCase()}
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#FF4D4D',
                animation: 'blink 1.5s infinite ease-in-out',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.4, transform: 'scale(0.8)' },
                },
              }}
            />
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              opacity: 0.9,
              letterSpacing: '0.02em',
            }}
          >
            {game.period} {formattedElapsed ? `— ${formattedElapsed}` : ''}
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            mt: 1,
            letterSpacing: '0.1em',
          }}
        >
          {status}
        </Typography>
      )}
    </>
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      {hasStartTime && startTimeObj ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {format(startTimeObj, 'h:mm a')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {format(startTimeObj, 'MMMM d')}
          </Typography>
        </>
      ) : (
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            mt: 1,
            letterSpacing: '0.1em',
          }}
        >
          UPCOMING
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        py: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', md: 'space-between' },
          alignItems: 'flex-start',
          width: '100%',
          px: { xs: 1, sm: 2, md: 4 },
          gap: { xs: 1, sm: 2, md: 0 },
        }}
      >
        {/* Team 1 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Avatar
            src={game.homeTeam?.logo}
            alt={game.homeTeam?.name}
            size={80}
            fallbackLabel={game.homeTeam?.abbreviation || game.homeTeam?.name?.substring(0, 3)}
            sx={{
              mb: { xs: 1, md: 1.5 },
              width: { xs: 48, sm: 64, md: 80 },
              height: { xs: 48, sm: 64, md: 80 },
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
              lineHeight: { xs: 1.2, md: 1.4 },
              maxHeight: { xs: '3em', md: 'none' },
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {game.homeTeam?.name}
          </Typography>
        </Box>

        {/* Score & Status */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: { xs: 0.5, sm: 2, md: 4 },
            minWidth: { xs: 70, sm: 120, md: 150 },
            flexShrink: 0,
            pt: { xs: 0.5, md: 0 },
          }}
        >
          {StatusDisplay}
        </Box>

        {/* Team 2 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Avatar
            src={game.awayTeam?.logo}
            alt={game.awayTeam?.name}
            size={80}
            fallbackLabel={game.awayTeam?.abbreviation || game.awayTeam?.name?.substring(0, 3)}
            sx={{
              mb: { xs: 1, md: 1.5 },
              width: { xs: 48, sm: 64, md: 80 },
              height: { xs: 48, sm: 64, md: 80 },
              bgcolor: game.awayTeam?.color || 'secondary.main',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
              lineHeight: { xs: 1.2, md: 1.4 },
              maxHeight: { xs: '3em', md: 'none' },
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {game.awayTeam?.name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
